<template>
    <div class="share-page">
        <CatLoading v-if="loading" />
        <div v-if="showEmpty" class="share-page-content">
            <CatEmpty class="share-page-empty" />
            <div>
                <div>{{ t('sharePage.statusContent') }}</div>
                <span class="share-page-go-home" @click="goHome">{{ t('sharePage.goHome') }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import qs from 'qs';
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { CatLoading, CatEmpty } from '@eureka/cat';
import { Site, SLD } from '@patsnap-utils/biz';
import { E_ShareType, getSharePath as getSharePathByType } from '@eureka/services/src/types/share';
import { ShareApiService, LinkStatus } from './share-api-service';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';

const { t } = useI18n();
const route = useRoute();
const shareApiService = injectService<ShareApiService>(ShareApiService);
const biTrackService = injectService<BiTrackService>(BiTrackService);

const share_id = route.params.share_id as string;
const loading = ref(true);
const status = ref<LinkStatus>(LinkStatus.VALID);

const invalidStatus = computed(() => {
    return [LinkStatus.EXPIRED, LinkStatus.INVALID].includes(status.value);
});

const showEmpty = computed(() => {
    return (invalidStatus.value || !share_id) && !loading.value;
});

const getShareDetailPath = (id: string, type: E_ShareType) => {
    const paths = {
        [E_ShareType.SEEK_SEARCH]: `/page/${id}`,
        [E_ShareType.TECH_MIND]: `#/board/${id}`,
        [E_ShareType.MATERIAL_MIND]: `#/material/board/${id}`,
        [E_ShareType.MATERIAL_MIND_V2]: `#/material/board/${id}`,
        [E_ShareType.TECH_RESEARCH]: `#/report/${id}`,
        [E_ShareType.NEWS_LETTER]: `#/detail/${id}`,
        [E_ShareType.AI_AGENT]: `/agent-flow/${id}`,
        [E_ShareType.PROJECT]: `#/graph/${id}`,
        [E_ShareType.AI_COMPANY_TECH_MONITOR]: `#/tech-monitor/${id}`,
        [E_ShareType.TRIZ_MIND]: `#/result/${id}`,
        [E_ShareType.IRA_CONVERSATION]: `/ai-search/${id}`,
    }
    return paths[type] || ''
}

const getShareDetailQuery = (type: E_ShareType) => {
    const paths = {
        [E_ShareType.AI_AGENT]: {
            reply: 1,
        },
        [E_ShareType.AI_COMPANY_TECH_MONITOR]: {
            company_tech_monitor: 1,
        },
    }
    return paths[type] || {};
}

const goHome = () => {
    window.location.href = '/home';
};

const parseShare = () => {
    loading.value = true;
    shareApiService.parseShare(share_id).then((res) => {
        const { data_type, data_id, link_status } = res;
        status.value = link_status;
        if (link_status === LinkStatus.VALID) {
            const pathname = getSharePathByType(data_type as E_ShareType)
            const path = getShareDetailPath(data_id, data_type as E_ShareType)
            const defaultQuery = getShareDetailQuery(data_type as E_ShareType)
            const url = `${new Site({ sld: SLD.EUREKA, pathname }).href}${path}`
            const queryString = qs.stringify({
                ...defaultQuery,
                ...route.query,
            });
            window.open(`${url}?${queryString}`, '_self')
        }
    }).finally(() => {
        loading.value = false;
    });
}

onMounted(() => {
    parseShare();
    biTrackService.biTrack(EurekaTrackEventType.SHARE_INFO, {
        sourceType: 'share-parse-page',
        eventDetailStr: {
            share_id,
        },
    });
});
</script>
<style lang="scss">
body {
    margin: 0;
    padding: 0;
}
.share-page {
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    .share-page-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 24px;
        color: #495973;
    }
    .share-page-empty {
        width: 124px;
        height: 115px;
    }
    .share-page-go-home {
        color: #0456D1;
        margin-top: 12px;
        cursor: pointer;
        &:hover {
            text-decoration: underline;
        }
    }
}
</style>
